import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useSectionVisibility } from '../config/sectionVisibility';

interface FloatingActionMenuProps {
  onLanguageToggle?: () => void;
  currentLanguage?: 'en' | 'pl';
}

const FloatingActionMenu: React.FC<FloatingActionMenuProps> = ({ 
  onLanguageToggle
}) => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const sectionVisibility = useSectionVisibility();

  // Generate language-aware navigation paths
  const getNavPath = (page: string) => {
    const baseRoute = language === 'en' ? '/en' : '';
    const routes = {
      contact: language === 'en' ? '/contact' : '/kontakt',
      services: language === 'en' ? '/services' : '/uslugi',
      technologies: language === 'en' ? '/technologies' : '/technologie'
    };
    return baseRoute + routes[page as keyof typeof routes];
  };

  // Direct navigation buttons (no dropdown) - filtered by visibility settings
  const allMenuItems = [
    {
      label: language === 'en' ? 'Contact' : 'Kontakt',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
      action: () => navigate(getNavPath('contact')),
      color: '#aa96da',
      visible: sectionVisibility.contactFloatingButton
    },
    {
      label: language === 'en' ? 'Services' : 'Usługi',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      action: () => navigate(getNavPath('services')),
      color: '#20b2aa',
      visible: sectionVisibility.servicesFloatingButton
    },
    {
      label: language === 'en' ? 'Technologies' : 'Technologie',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
        </svg>
      ),
      action: () => navigate(getNavPath('technologies')),
      color: '#f59e0b',
      visible: sectionVisibility.technologiesFloatingButton
    },
    {
      label: language === 'en' ? 'EN' : 'PL',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
        </svg>
      ),
      action: onLanguageToggle || (() => {}),
      color: '#ef4444',
      visible: sectionVisibility.languageFloatingButton
    }
  ];
  
  // Filter out hidden items
  const menuItems = allMenuItems.filter(item => item.visible);

  return (
    <motion.div
      className="fixed right-4 top-20 z-[9999] flex flex-col space-y-3"
      initial={{ opacity: 0, scale: 0 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: 1, type: "spring", stiffness: 260, damping: 20 }}
    >
      {menuItems.length > 0 && menuItems.map((item, index) => (
        <motion.button
          key={item.label}
          className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-gray-900/80 backdrop-blur-md border border-gray-700/50 flex items-center justify-center shadow-2xl group"
          onClick={item.action}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ 
            delay: 1 + index * 0.1,
            type: "spring", 
            stiffness: 260, 
            damping: 20 
          }}
          style={{
            background: `linear-gradient(135deg, rgba(17, 24, 39, 0.8), rgba(17, 24, 39, 0.6))`
          }}
        >
          <motion.div
            className="text-gray-300 group-hover:text-white transition-colors"
            style={{ color: item.color }}
            whileHover={{ scale: 1.2 }}
          >
            {item.icon}
          </motion.div>
          
          {/* Tooltip - Hidden on mobile */}
          <div className="hidden md:block absolute right-16 top-1/2 transform -translate-y-1/2 bg-gray-900/90 backdrop-blur-md border border-gray-700/50 rounded-lg px-3 py-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap">
            <span className="text-sm font-medium text-white">{item.label}</span>
          </div>
        </motion.button>
      ))}

      {/* Floating Particles Effect */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        animate={{
          background: [
            'radial-gradient(circle at 50% 50%, rgba(170, 150, 218, 0.1) 0%, transparent 50%)',
            'radial-gradient(circle at 60% 40%, rgba(32, 178, 170, 0.1) 0%, transparent 50%)',
            'radial-gradient(circle at 40% 60%, rgba(170, 150, 218, 0.1) 0%, transparent 50%)'
          ]
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          repeatType: "reverse"
        }}
      />
    </motion.div>
  );
};

export default FloatingActionMenu;
